import React from 'react';
import { itineraries } from '../data/mockData';
import { Clock, TrendingUp, Download } from 'lucide-react';

const Itineraries = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/villa-nabila-johor-2.jpg"
            alt="Itineraries"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <h1 className="heading-1 text-[#dc2626] mb-6 max-w-3xl">
            Dark Tourism Itineraries
          </h1>
          <p className="body-large text-white/90 max-w-3xl">
            Suggested routes and travel plans for exploring Malaysia's dark tourism heritage by region and theme.
          </p>
        </div>
      </section>

      {/* Itineraries Grid */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {itineraries.map((itinerary) => (
                <div 
                  key={itinerary.id}
                  className="bg-[#1a1c1b] rounded-lg overflow-hidden border border-[#3a2222] hover:border-[#dc2626]/50 transition-all group"
                >
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <span className="caption text-[#dc2626]">Itinerary #{itinerary.id}</span>
                      <div className="flex items-center gap-4 text-[#888680]">
                        <span className="flex items-center gap-2">
                          <Clock size={16} />
                          <span className="caption">{itinerary.duration}</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <TrendingUp size={16} />
                          <span className="caption">{itinerary.difficulty}</span>
                        </span>
                      </div>
                    </div>

                    <h2 className="heading-3 text-white mb-4">{itinerary.title}</h2>
                    
                    <p className="body-medium text-white/70 mb-6">
                      {itinerary.description}
                    </p>

                    <button className="btn-secondary text-sm w-full group-hover:bg-[#dc2626] group-hover:text-white group-hover:border-[#dc2626]">
                      <Download className="mr-2" size={16} />
                      Download PDF (Coming Soon)
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Features */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-2 text-[#dc2626] mb-8">More Itineraries Coming Soon</h2>
            
            <p className="body-medium text-white/80 mb-12">
              We're actively developing comprehensive travel guides for each region, including:
            </p>

            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Regional Routes</h3>
                <ul className="space-y-2 body-small text-white/70">
                  <li>• Northern Peninsula (Penang, Kedah, Perlis)</li>
                  <li>• Central Malaysia (Selangor, KL, Negeri Sembilan)</li>
                  <li>• Southern Route (Johor, Malacca)</li>
                  <li>• East Coast Trail (Kelantan, Terengganu, Pahang)</li>
                  <li>• Sabah & Sarawak Heritage</li>
                </ul>
              </div>

              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Thematic Routes</h3>
                <ul className="space-y-2 body-small text-white/70">
                  <li>• WWII Pacific War Trail</li>
                  <li>• Colonial Heritage Circuit</li>
                  <li>• Emergency Era Sites</li>
                  <li>• Medical History Tour</li>
                  <li>• Cemetery & Memorial Trail</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning Tips */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8">Planning Your Visit</h2>
            
            <div className="space-y-6 body-medium text-white/80">
              <div className="bg-[#1a1c1b] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <h3 className="heading-4 text-white mb-4">Before You Go</h3>
                <ul className="space-y-3 body-small text-white/70">
                  <li>• Research each site's history and significance</li>
                  <li>• Check opening hours and accessibility (some sites are abandoned/restricted)</li>
                  <li>• Prepare appropriate clothing (modest dress for religious/memorial sites)</li>
                  <li>• Review our <a href="/guidelines" className="text-[#dc2626] hover:underline">Visitor Guidelines</a></li>
                  <li>• Consider hiring a knowledgeable local guide</li>
                </ul>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <h3 className="heading-4 text-white mb-4">What to Bring</h3>
                <ul className="space-y-3 body-small text-white/70">
                  <li>• Camera for documentary photography (used respectfully)</li>
                  <li>• Notebook for reflections</li>
                  <li>• Water and sun protection</li>
                  <li>• Insect repellent (many sites are outdoor/rural)</li>
                  <li>• Small donation for site maintenance where applicable</li>
                </ul>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <h3 className="heading-4 text-white mb-4">Safety Considerations</h3>
                <ul className="space-y-3 body-small text-white/70">
                  <li>• Some sites are structurally unsafe - exercise caution</li>
                  <li>• Abandoned locations may lack proper facilities</li>
                  <li>• Always respect "No Entry" signs and private property</li>
                  <li>• Travel with companions when visiting remote areas</li>
                  <li>• Inform someone of your itinerary</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Itineraries;
